import * as React from 'react';
import type {PanelName, PanelTransitionBlocker} from '../types';

type UnsubscribeFn = () => void;
type AddTransitionBlocker = (
  PANEL_NAME: PanelName,
  transitionBlocker: PanelTransitionBlocker
) => UnsubscribeFn;
type TransitionBlockerHook = (
  PANEL_NAME: PanelName,
  transitionBlocker: PanelTransitionBlocker
) => void;

// we'll wrap the state in a closure so that we can
// test the behaviour in isolation
export const init = () => {
  // the blockers are stored in an object as sets of
  // functions associated with each panel name
  // e.g. { PANEL_NAME: new Set([blocker1, blocker2]) }
  const onExitBlockers: Record<string, Set<PanelTransitionBlocker>> = {};

  // the API we expose to the world outside of the
  // package is a single function which adds one
  // exit blocker at a time
  const addExitBlocker: AddTransitionBlocker = (
    PANEL_NAME,
    transitionBlocker
  ) => {
    // if there are no blockers registered for the panel
    // yet, we create the set
    if (!onExitBlockers[PANEL_NAME]) {
      onExitBlockers[PANEL_NAME] = new Set();
    }
    // then we add the blocker to the set
    onExitBlockers[PANEL_NAME]?.add(transitionBlocker);

    // we return a cleanup function which allows the consumer
    // to remove the blocker from the set
    return () => {
      // when called, first we remove the blocker from the set
      onExitBlockers[PANEL_NAME]?.delete(transitionBlocker);
      // then we check whether there are any other blockers
      // for the panel; if there are none, we remove the
      // set from the map so it can be garbage collected
      if (onExitBlockers[PANEL_NAME]?.size === 0) {
        // eslint-disable-next-line webflow/no-delete
        delete onExitBlockers[PANEL_NAME];
      }
    };
  };

  const useExitBlocker: TransitionBlockerHook = (
    PANEL_NAME,
    transitionBlocker
  ) => {
    React.useEffect(
      () => addExitBlocker(PANEL_NAME, transitionBlocker),
      [PANEL_NAME, transitionBlocker]
    );
  };

  return {
    onExitBlockers,
    addExitBlocker,
    useExitBlocker,
  };
};

// we'll also create and export a single instance
// for use outside of testing
export const {onExitBlockers, addExitBlocker, useExitBlocker} = init();
